import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, type Me } from "../api.js";
import { useAuth } from "../auth.js";
import { EditRecurringLesson } from "../booking/EditRecurringLesson.js";
import { reportPeople } from "../booking/people.js";
import { recurringLessons, type RecurrencePlanSummary } from "../booking/recurrences.js";
import { staffBooking } from "../booking/staff.js";
import { formatDate } from "../format.js";
import { ActionConfirmation, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { ConfirmDialog } from "../ui/Dialog.js";
import { useAsync } from "../useAsync.js";
import "./studentRecurringLessons.css";

type NamedPlan = { plan: RecurrencePlanSummary; tutor: string; service: string };
type Props = { studentId: string; studentName: string; refreshRevision?: number };

const STATUS_LABELS = { active: "Active", paused: "Paused", cancelled: "Stopped", completed: "Completed" };

function assertStudent(plan: RecurrencePlanSummary, studentId: string, planId = plan?.id) {
  if (!plan || !plan.id || plan.id !== planId || plan.portal_student_id !== studentId || !Object.hasOwn(STATUS_LABELS, plan.status)) {
    throw new Error("The recurring plan could not be confirmed for this student. Reload the plans before trying again.");
  }
}

async function loadPlans(studentId: string): Promise<NamedPlan[]> {
  const result = await staffBooking.recurrences(studentId);
  if (result.count !== result.items.length) {
    throw new Error("The recurring plan list is incomplete. Reload the plans before relying on this status or history.");
  }
  result.items.forEach((plan) => assertStudent(plan, studentId));
  if (!result.items.length) return [];
  // Names are supplementary: an unavailable catalogue must not hide an active plan.
  const providers = [...new Set(result.items.map((plan) => plan.provider_id))];
  const [people, services] = await Promise.all([
    reportPeople().catch(() => []),
    Promise.all(providers.map(async (id) => ({ id, items: await staffBooking.providerServices(id).catch(() => []) }))),
  ]);
  return result.items.map((plan) => ({
    plan,
    tutor: people.find((person) => person.providerId === plan.provider_id)?.name ?? "Tutor unavailable",
    service: services.find((item) => item.id === plan.provider_id)?.items.find((item) => item.id === plan.service_id)?.name ?? "Lesson type unavailable",
  }));
}

function schedule(plan: RecurrencePlanSummary): string {
  // WMF-SIte stores the first local lesson date as starts_on and advances by
  // whole weeks. Contract verified 20 October 2026; see PENDING-RELEASE.md.
  const weekday = new Intl.DateTimeFormat("en-GB", { weekday: "long", timeZone: "Europe/London" })
    .format(new Date(`${plan.starts_on}T12:00:00Z`));
  return `${plan.cadence === "fortnightly" ? "Every two weeks" : "Weekly"} on ${weekday} at ${plan.start_time.slice(0, 5)}`;
}

function identity(item: NamedPlan, studentName: string): string {
  return `${studentName} · ${item.tutor} · ${item.service} · ${schedule(item.plan)} · from ${formatDate(`${item.plan.starts_on}T12:00:00Z`)}`;
}

export function StudentRecurringLessons(props: Props) {
  const { me } = useAuth();
  if (!me?.booking.staff) return null;
  // Unmount drafts, confirmations and pending reads when the student or authority changes.
  const scope = JSON.stringify([props.studentId, me.user.id, me.booking, me.capabilities]);
  return <StudentRecurringLessonsContent key={scope} {...props} me={me} />;
}

function StudentRecurringLessonsContent({ studentId, studentName, refreshRevision = 0, me }: Props & { me: Me }) {
  const headingId = useId();
  const plans = useAsync(() => loadPlans(studentId), [studentId, refreshRevision]);
  const [editing, setEditing] = useState<NamedPlan | null>(null);
  const [stopping, setStopping] = useState<NamedPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const writing = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  // Keep this student's last confirmed cards during a refresh. Controls stay
  // disabled until the fresh read completes; a failed read still hides them.
  const loaded = !plans.error ? plans.data : null;
  const current = loaded?.filter(({ plan }) => plan.status === "active" || plan.status === "paused") ?? [];
  const past = loaded?.filter(({ plan }) => plan.status === "cancelled" || plan.status === "completed") ?? [];
  const activeCount = current.filter(({ plan }) => plan.status === "active").length;
  const allTutors = me.capabilities.includes("recurrences.read.all");
  const canManage = (plan: RecurrencePlanSummary) =>
    me.capabilities.includes("appointments.manage.all") || me.booking.providerId === plan.provider_id;

  function stopped(item: NamedPlan, alreadyStopped = false): ActionOutcome {
    return {
      variant: "success", title: alreadyStopped ? "Recurring plan was already stopped" : "Recurring lessons stopped", description: identity(item, studentName),
      details: [{ value: "This plan will no longer create future lessons. Lessons already in the diary remain booked." }],
      action: <Link to={`/students/${encodeURIComponent(studentId)}/cancel-future`}>Review lessons already booked</Link>,
    };
  }

  async function stop() {
    if (!stopping || writing.current) return;
    const item = stopping;
    writing.current = true;
    setBusy(true);
    setOutcome(null);
    let attempted = false;
    try {
      const before = await recurringLessons.detail(item.plan.id);
      if (!alive.current) return;
      assertStudent(before.plan, studentId, item.plan.id);
      if (before.plan.status !== "cancelled") {
        if ((["provider_id", "service_id", "starts_on", "start_time", "ends_on", "cadence"] as const)
          .some((field) => before.plan[field] !== item.plan[field])) {
          throw new Error("This plan changed while the confirmation was open. Reload the plans and review its current details before stopping it.");
        }
        if (!["active", "paused"].includes(before.plan.status) || !canManage(before.plan)) {
          throw new Error("This plan can no longer be stopped here. Reload the plans to check its current status.");
        }
        attempted = true;
        const result = await recurringLessons.stop(item.plan.id);
        assertStudent(result.plan, studentId, item.plan.id);
        const confirmed = await recurringLessons.detail(item.plan.id);
        assertStudent(confirmed.plan, studentId, item.plan.id);
        if (confirmed.plan.status !== "cancelled") throw new Error("The stopped status was not confirmed.");
      }
      if (alive.current) setOutcome(stopped(item, before.plan.status === "cancelled"));
    } catch (cause) {
      const rejected = cause instanceof ApiError && [400, 401, 403, 404, 409, 422].includes(cause.status);
      let confirmedStopped = false;
      if (attempted && !rejected) {
        try {
          const confirmed = await recurringLessons.detail(item.plan.id);
          assertStudent(confirmed.plan, studentId, item.plan.id);
          confirmedStopped = confirmed.plan.status === "cancelled";
        } catch { /* Keep an uncertain result until the authoritative plan can be read. */ }
      }
      if (alive.current) setOutcome(confirmedStopped ? stopped(item) : {
        variant: attempted && !rejected ? "uncertain" : "attention",
        title: attempted && !rejected ? "Recurring plan status needs checking" : "Recurring plan was not stopped",
        description: identity(item, studentName),
        details: [{ value: attempted && !rejected
          ? "We could not confirm whether the plan stopped. Reload the plans to check before trying again."
          : cause instanceof Error ? cause.message : "The plan could not be stopped. Reload the plans and try again." }],
      });
    } finally {
      if (alive.current) {
        plans.reload();
        setStopping(null);
        setBusy(false);
      }
      writing.current = false;
    }
  }

  function card(item: NamedPlan) {
    const { plan, tutor, service } = item;
    const editable = plan.status === "active" || plan.status === "paused";
    return <article className="student-recurring-plan" key={plan.id} aria-label={`${service} with ${tutor}, ${schedule(plan)}, from ${formatDate(`${plan.starts_on}T12:00:00Z`)}`}>
      <div className="student-recurring-plan-copy">
        <div className="student-recurring-plan-title"><h3>{service}</h3><span className={`tag${plan.status === "active" ? " student-recurring-on" : ""}`}>{STATUS_LABELS[plan.status]}</span></div>
        <p>{tutor} · <strong>{schedule(plan)}</strong></p>
        <p className="small muted">From {formatDate(`${plan.starts_on}T12:00:00Z`)}{plan.ends_on ? ` until ${formatDate(`${plan.ends_on}T12:00:00Z`)}` : " · no fixed end date"} · UK time</p>
        {plan.status === "paused" && <p className="small muted">Paused: this plan is not creating new lessons.</p>}
      </div>
      {editable && canManage(plan) && <div className="student-recurring-actions">
        <button type="button" className="action-secondary" disabled={plans.loading || busy || editing !== null} onClick={() => { setOutcome(null); setEditing(item); }}>Edit</button>
        <button type="button" className="action-danger" disabled={plans.loading || busy || editing !== null} onClick={() => { setOutcome(null); setStopping(item); }}>Stop recurring plan</button>
      </div>}
    </article>;
  }

  return <section className="panel student-recurring-lessons" aria-labelledby={headingId} aria-busy={plans.loading}>
    <div className="student-recurring-heading">
      <div><h2 id={headingId}>Recurring lessons</h2><p className="small muted">{allTutors ? "Repeating plans for this student." : "Repeating plans you can access for this student."}</p></div>
      {loaded && <span className={`tag${activeCount ? " student-recurring-on" : ""}`}>{activeCount ? `On · ${activeCount} active ${activeCount === 1 ? "plan" : "plans"}` : current.length ? "Paused" : allTutors ? "No active plans" : "No active plans visible"}</span>}
    </div>
    {outcome && <ActionConfirmation {...outcome} reveal action={outcome.action ?? (outcome.variant !== "success" && <button type="button" className="action-secondary" disabled={plans.loading} onClick={() => { setOutcome(null); plans.reload(); }}>Reload recurring plans</button>)} />}
    {plans.loading && <p role="status" className="small muted">{loaded ? "Refreshing recurring lessons…" : "Checking recurring lessons…"}</p>}
    {plans.error && <div role="alert"><p>Recurring lesson status is unavailable. {plans.error}</p><button type="button" className="action-secondary" onClick={plans.reload}>Reload recurring plans</button></div>}
    {loaded && <>
      {!current.length && <p className="small">{allTutors ? "No active recurring plan is creating new lessons for this student." : "No active recurring plans are visible to you for this student."}</p>}
      {current.map(card)}
      {past.length > 0 && <details className="student-recurring-history"><summary>Stopped and completed plans ({past.length})</summary>{past.map(card)}</details>}
      {(current.length > 0 || past.length > 0) && <p className="small muted student-recurring-help">Stopping a plan prevents new automatic bookings. Lessons already in the diary stay booked and can be reviewed separately.</p>}
    </>}
    <Link className="small" to="/recurring-lessons">Manage recurring lessons</Link>
    {editing && <EditRecurringLesson planId={editing.plan.id} studentId={studentId} identity={identity(editing, studentName)} onClose={() => setEditing(null)} onOutcome={(next) => { setOutcome(next); plans.reload(); }} />}
    {stopping && <ConfirmDialog title="Stop recurring lessons?" description={identity(stopping, studentName)} confirmLabel="Stop recurring plan" confirmingLabel="Stopping…" confirmClassName="action-danger" busy={busy} onClose={() => setStopping(null)} onConfirm={stop}>
      <p>This stops this plan from creating new lessons. Lessons already in the diary stay booked.</p>
      <p>A stopped plan cannot be resumed here. To take a temporary break, use Pause on the Recurring lessons page.</p>
    </ConfirmDialog>}
  </section>;
}
